import React from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Card, Skeleton } from 'antd'

import { GET_ORDER_JOURNEY } from '../../../graphql/orderJourney'
import { orderItemIds } from './journeyFacts'
import { JourneyFrame, Muted, useJourneyColorVars } from './journeyStyles'
import { JourneySummary } from './JourneySummary'
import { JourneySteps } from './JourneySteps'

const languageOf = (i18n) => (i18n.language === 'en' ? 'en' : 'th')

const JourneyCard = ({ children }) => {
  const { t } = useTranslation('inquiries')
  return (
    <Card size='small' title={t('orderJourney.title')}>
      {children}
    </Card>
  )
}

const JourneyContent = ({ orderNumber }) => {
  const { t, i18n } = useTranslation('inquiries')
  const { data, loading, error, refetch } = useQuery(GET_ORDER_JOURNEY, {
    variables: { orderNumber },
    skip: !orderNumber,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  })
  const order = data?.order

  if (loading && !order) {
    return (
      <JourneyCard>
        <Skeleton active paragraph={{ rows: 6 }} />
      </JourneyCard>
    )
  }

  if (error) {
    return (
      <JourneyCard>
        <Alert
          type='warning'
          showIcon
          message={t('orderJourney.loadError')}
          description={error.message}
          action={<Button size='small' onClick={() => refetch()}>{t('orderJourney.retry')}</Button>}
        />
      </JourneyCard>
    )
  }

  const journey = order?.cdpJourney
  if (!journey) {
    return (
      <JourneyCard>
        <Muted>{t('orderJourney.noRecord')}</Muted>
      </JourneyCard>
    )
  }

  const lang = languageOf(i18n)
  const orderIds = orderItemIds(order.lineItems)
  return (
    <>
      <JourneySummary journey={journey} orderIds={orderIds} lang={lang} />
      <JourneySteps journey={journey} orderIds={orderIds} lang={lang} />
    </>
  )
}

const OrderJourney = ({ orderNumber }) => {
  const colorVars = useJourneyColorVars()
  return (
    <JourneyFrame style={colorVars}>
      <JourneyContent orderNumber={orderNumber} />
    </JourneyFrame>
  )
}

const orderNumberProps = {
  orderNumber: PropTypes.string.isRequired
}

OrderJourney.propTypes = orderNumberProps
JourneyContent.propTypes = orderNumberProps
JourneyCard.propTypes = {
  children: PropTypes.node.isRequired
}

export { OrderJourney }
